import React from 'react';
import PropTypes from 'prop-types';
import { LoadingButton, Modal } from '@spotify-internal/anchor-ds';
import { ButtonSecondary } from '@spotify-internal/encore-web';
import { useT } from 'client/i18n/hooks/useT';
import { EditTextScene } from './components/EditTextScene';
import { ImageSearchScene } from './components/ImageSearchScene';
import { ChoosePathScene } from './components/ChoosePathScene';

import styles from './CoverArtModalScreen.sass';

const noop = () => null;

const getSourceImageUrlToCropFromSourceImage = sourceImage => {
  if (sourceImage.maybeLocalImageBlobUrl) {
    return sourceImage.maybeLocalImageBlobUrl;
  }
  if (sourceImage.maybeHttpImageUrl) {
    return sourceImage.maybeHttpImageUrl;
  }
  return undefined;
};

const CoverArtModalScreen = ({
  podcastName,
  isShowing,
  isFetching,
  coverArt,
  onPressNavigateBack,
  isHidingNavigateBackButtonOnChooseScene,
  scene,
  onChooseSearchPath,
  onChooseUploadPath,
  onChooseRandomPath,
  //
  isHidingImageSearch,
  unsplashImages,
  searchText,
  onSearchTextChange,
  onRequestMoreItems,
  onChangeZoomLevel,
  zoomLevel,
  xCropPosition,
  yCropPosition,
  onCropPositionChange,
  onSelectImage,
  isLoadingImageToCrop,
  onCropComplete,
  onSubmitCroppedPhoto,
  onClickClose,
  onToggleApplyText,
  isColorPickerShowing,
  onClickColorPickerHide,
  onClickColorPickerShow,
  textImageUrl,
  maybeSelectedFontId,
  isTextImageLoading,
  textHex,
  isTextShowing,
  isTextColorLoading,
  onSelectFont,
  onSelectJustification,
  justification,
  onSelectAlignment,
  alignment,
  onSelectColor,
  //
  onSubmitCoverArt,
  isAnchorWatermarkShowing,
  setHasAnchorBranding,
  imageErrorText,
}) => {
  const t = useT();
  const isShowingBackButton =
    scene !== 'choose_path' ||
    (!isHidingNavigateBackButtonOnChooseScene && scene === 'choose_path');

  return (
    <div className={styles.root}>
      <Modal
        isShowing={isShowing}
        isShowingCloseButton
        onClose={onClickClose}
        aria-label={t(
          'CoverArtModalScreen.ModalAriaLabel',
          'Update cover art',
          'Update cover art modal aria label'
        )}
      >
        {scene === 'choose_path' && (
          <ChoosePathScene
            imageErrorText={imageErrorText}
            coverArtImageUrl={getSourceImageUrlToCropFromSourceImage(
              coverArt.sourceImage
            )}
            onChooseSearchPath={() => onChooseSearchPath(podcastName)}
            onChooseRandomPath={() => onChooseRandomPath(podcastName)}
            onChooseUploadPath={onChooseUploadPath}
          />
        )}
        {scene === 'image_search' && (
          <ImageSearchScene
            coverArt={coverArt}
            unsplashImages={unsplashImages}
            searchText={searchText}
            onSearchTextChange={onSearchTextChange}
            onRequestMoreItems={onRequestMoreItems}
            onChangeZoomLevel={onChangeZoomLevel}
            zoomLevel={zoomLevel}
            xCropPosition={xCropPosition}
            yCropPosition={yCropPosition}
            onCropPositionChange={onCropPositionChange}
            onSelectImage={onSelectImage}
            onCropComplete={onCropComplete}
            isHidingImageSearch={isHidingImageSearch}
            isLoadingImageToCrop={isLoadingImageToCrop}
          />
        )}
        {scene === 'text_edit' && (
          <EditTextScene
            croppedCoverArtImageUrl={coverArt.maybeCroppedImageUrl || ''}
            onToggleApplyText={onToggleApplyText}
            textImageUrl={textImageUrl}
            textHex={textHex}
            isTextShowing={isTextShowing}
            onSelectFont={onSelectFont}
            onSelectJustification={onSelectJustification}
            isColorPickerShowing={isColorPickerShowing}
            onClickColorPickerHide={onClickColorPickerHide}
            onClickColorPickerShow={onClickColorPickerShow}
            justification={justification}
            onSelectAlignment={onSelectAlignment}
            alignment={alignment}
            maybeSelectedFontId={maybeSelectedFontId}
            isTextImageLoading={isTextImageLoading}
            onSelectColor={onSelectColor}
            isAnchorWatermarkShowing={isAnchorWatermarkShowing}
            setHasAnchorBranding={setHasAnchorBranding}
            isTextColorLoading={isTextColorLoading}
          />
        )}

        <Modal.Footer>
          {isShowingBackButton && (
            <ButtonSecondary onClick={() => onPressNavigateBack(scene)}>
              {t(
                'CoverArtModalScreen.BackButton',
                'Back',
                'Back through to the previous step in the cover art modal screen process'
              )}
            </ButtonSecondary>
          )}
          {scene === 'image_search' && (
            <LoadingButton
              isLoading={isFetching}
              onClick={onSubmitCroppedPhoto}
            >
              {t(
                'CoverArtModalScreen.ContinueButton',
                'Continue',
                'Continue through to the next step in the cover art modal screen process'
              )}
            </LoadingButton>
          )}
          {scene === 'text_edit' && (
            <LoadingButton isLoading={isFetching} onClick={onSubmitCoverArt}>
              {t(
                'CoverArtModalScreen.UpdateCoverArtButton',
                'Update cover art',
                'Update cover art to complete the cover art modal screen process'
              )}
            </LoadingButton>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
};

const unSplashImagePropType = PropTypes.shape({
  thumbnailUrl: PropTypes.string.isRequired,
  fullResUrl: PropTypes.string.isRequired,
  id: PropTypes.string,
  authorName: PropTypes.string,
});

const coverArtPropType = PropTypes.shape({
  maybeCroppedImageUrl: PropTypes.string,
  maybeFinalImageUrl: PropTypes.string,
  sourceImage: PropTypes.shape({
    maybeUnsplashImage: unSplashImagePropType,
    maybeLocalImageBlobUrl: PropTypes.string,
    maybeHttpImageUrl: PropTypes.string,
  }),
});

CoverArtModalScreen.defaultProps = {
  podcastName: null,
  isShowing: false,
  scene: 'image_search',
  //
  onChooseSearchPath: noop,
  onChooseUploadPath: noop,
  onChooseRandomPath: noop,
  //
  onToggleApplyText: noop,
  textImageUrl: '',
  textHex: '#ffffff',
  isTextShowing: true,
  onSelectFont: noop,
  onSelectJustification: noop,
  justification: 'center',
  onSelectAlignment: noop,
  alignment: 'top',
  onSelectColor: noop,
  unsplashImages: [],
  searchText: '',
  zoomLevel: 1,
  onSelectImage: noop,
  xCropPosition: 0,
  yCropPosition: 0,
  isTextColorLoading: false,
  onSearchTextChange: noop,
  onRequestMoreItems: noop,
  onChangeZoomLevel: noop,
  onSubmitCroppedPhoto: noop,
  onCropPositionChange: noop,
  onCropComplete: noop,
  imageErrorText: null,
  //
  onSubmitCoverArt: noop,
  isHidingImageSearch: false,
  isLoadingImageToCrop: false,
  isFetching: false,
  maybeSelectedFontId: null,
  isTextImageLoading: false,
  onPressNavigateBack: noop,
  isHidingNavigateBackButtonOnChooseScene: false,
  isColorPickerShowing: false,
  isAnchorWatermarkShowing: false,
  onClickColorPickerHide: noop,
  onClickColorPickerShow: noop,
  onClickClose: noop,
};

CoverArtModalScreen.propTypes = {
  podcastName: PropTypes.string,
  isShowing: PropTypes.bool,
  scene: PropTypes.oneOf(['text_edit', 'image_search', 'choose_path']),
  //
  onChooseSearchPath: PropTypes.func,
  onChooseUploadPath: PropTypes.func,
  onChooseRandomPath: PropTypes.func,
  //
  coverArt: coverArtPropType.isRequired,
  textImageUrl: PropTypes.string,
  textHex: PropTypes.string,
  isTextShowing: PropTypes.bool,
  onSelectFont: PropTypes.func,
  onToggleApplyText: PropTypes.func,
  onSelectJustification: PropTypes.func,
  justification: PropTypes.oneOf(['left', 'center', 'right']),
  onSelectAlignment: PropTypes.func,
  alignment: PropTypes.oneOf(['top', 'center', 'bottom']),
  onSelectColor: PropTypes.func,
  isTextColorLoading: PropTypes.bool,
  unsplashImages: PropTypes.arrayOf(unSplashImagePropType),
  searchText: PropTypes.string,
  onSearchTextChange: PropTypes.func,
  onRequestMoreItems: PropTypes.func,
  onChangeZoomLevel: PropTypes.func,
  zoomLevel: PropTypes.number,
  onSelectImage: PropTypes.func,
  xCropPosition: PropTypes.number,
  yCropPosition: PropTypes.number,
  onSubmitCroppedPhoto: PropTypes.func,
  onCropPositionChange: PropTypes.func,
  onCropComplete: PropTypes.func,
  isHidingImageSearch: PropTypes.bool,
  imageErrorText: PropTypes.string,
  //
  onSubmitCoverArt: PropTypes.func,
  isLoadingImageToCrop: PropTypes.bool,
  isFetching: PropTypes.bool,
  maybeSelectedFontId: PropTypes.string,
  isTextImageLoading: PropTypes.bool,
  onPressNavigateBack: PropTypes.func,
  isHidingNavigateBackButtonOnChooseScene: PropTypes.bool,
  isColorPickerShowing: PropTypes.bool,
  isAnchorWatermarkShowing: PropTypes.bool,
  onClickColorPickerHide: PropTypes.func,
  onClickColorPickerShow: PropTypes.func,
  onClickClose: PropTypes.func,
};

export default CoverArtModalScreen;
